/**
 * Generic listing-page parser — reads JSON-LD + microdata + Open Graph
 * tags. Used as the default `parseDetailHtml` for adapters that don't
 * declare a site-specific overlay. Per the overlay contract this parser
 * contributes to the field-level merge policy in merge-parser-data.ts.
 *
 * Targets the four fields the poisoned-data hunts surfaced as
 * Firecrawl-extraction risk: address, salePrice, sale_date, bed/bath/
 * parking. Sites tend to expose at least one of:
 *   - <script type="application/ld+json"> with @type RealEstateListing
 *     or Place + offers/numberOfBedrooms/numberOfBathroomsTotal
 *   - itemprop="price" / "streetAddress" / "numberOfBedrooms" microdata
 *   - <meta property="og:title"> + <meta property="product:price:amount">
 *
 * This is heuristic — when a site doesn't expose structured data the
 * parser returns {} and the provider extract stands. See the per-adapter
 * parsers (kay-burton-bayside/parser.ts, marshall-white/parser.ts,
 * realestate-com-au/parser.ts) for site-specific overlays that pick up
 * fields beyond what's in JSON-LD.
 */

interface JsonLdNode {
  "@type"?: string | string[];
  "@graph"?: JsonLdNode[];
  name?: string;
  address?:
    | string
    | {
        streetAddress?: string;
        addressLocality?: string;
        addressRegion?: string;
        postalCode?: string;
      };
  offers?: {
    price?: number | string;
    priceCurrency?: string;
    availability?: string;
  };
  numberOfBathroomsTotal?: number | string;
  numberOfBedrooms?: number | string;
  numberOfFullBathrooms?: number | string;
  floorSize?: { value?: number | string };
}

function safeParseJsonLd(html: string): JsonLdNode[] {
  const out: JsonLdNode[] = [];
  const re = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  for (const match of html.matchAll(re)) {
    const body = (match[1] ?? "").trim();
    if (!body) continue;
    try {
      const parsed = JSON.parse(body) as JsonLdNode | JsonLdNode[];
      if (Array.isArray(parsed)) out.push(...parsed);
      else if (parsed && typeof parsed === "object") out.push(parsed);
    } catch {
      // Malformed JSON-LD is common (sites pre-pend code); skip silently.
    }
  }
  // Flatten @graph wrapper used by Yoast-style schemas.
  const flat: JsonLdNode[] = [];
  for (const node of out) {
    if (Array.isArray(node["@graph"])) flat.push(...(node["@graph"] ?? []));
    flat.push(node);
  }
  return flat;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.]/g, "");
    if (!cleaned) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatAddress(addr: NonNullable<JsonLdNode["address"]>): string | null {
  if (typeof addr === "string") return addr.trim() || null;
  const parts: string[] = [];
  if (addr.streetAddress) parts.push(addr.streetAddress.trim());
  const localityParts: string[] = [];
  if (addr.addressLocality) localityParts.push(addr.addressLocality.trim());
  if (addr.addressRegion) localityParts.push(addr.addressRegion.trim().toUpperCase());
  if (addr.postalCode) localityParts.push(addr.postalCode.trim());
  if (localityParts.length > 0) parts.push(localityParts.join(" "));
  return parts.length > 0 ? parts.join(", ") : null;
}

function extractMetaContent(html: string, property: string): string | null {
  const re = new RegExp(
    `<meta\\s+[^>]*property=["']${property.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}["'][^>]*content=["']([^"']*)["']`,
    "i",
  );
  const m = html.match(re);
  return m ? m[1].trim() : null;
}

function extractMicrodataNumber(html: string, prop: string): number | null {
  // <span itemprop="numberOfBedrooms">4</span>
  const re = new RegExp(
    `itemprop=["']${prop.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}["'][^>]*>([^<]+)`,
    "i",
  );
  const m = html.match(re);
  if (!m) return null;
  const n = asNumber(m[1]);
  return n !== null && n >= 0 && n <= 30 ? n : null;
}

export function parseGenericListingHtml(
  html: string,
  _url: string,
): Record<string, unknown> {
  if (!html || html.length === 0) return {};
  const result: Record<string, unknown> = {};
  const nodes = safeParseJsonLd(html);

  // Prefer the first listing-shaped node; fall back to any node with
  // address or offers. Sites occasionally emit Organization-level JSON-LD
  // before the listing one — skip those.
  const listing =
    nodes.find((n) => {
      const type = Array.isArray(n["@type"]) ? n["@type"].join(",") : n["@type"] ?? "";
      return /RealEstate|Place|Residence|Product|SingleFamilyResidence|Apartment|House/i.test(type) && (n.address || n.offers);
    }) ??
    nodes.find((n) => n.address && n.offers) ??
    null;

  if (listing) {
    if (listing.address) {
      const formatted = formatAddress(listing.address);
      if (formatted) result.address = formatted;
    }
    if (listing.offers?.price !== undefined) {
      const price = asNumber(listing.offers.price);
      if (price !== null && price >= 1000 && price < 1_000_000_000) {
        result.salePrice = price;
      }
    }
    const bed = asNumber(listing.numberOfBedrooms);
    if (bed !== null && bed > 0 && bed <= 20) result.bedrooms = bed;
    const bath = asNumber(listing.numberOfBathroomsTotal ?? listing.numberOfFullBathrooms);
    if (bath !== null && bath > 0 && bath <= 20) result.bathrooms = bath;
  }

  // Microdata fallbacks — only fill missing fields. Many small agency sites
  // emit microdata instead of JSON-LD.
  if (!result.bedrooms) {
    const bed = extractMicrodataNumber(html, "numberOfBedrooms");
    if (bed !== null) result.bedrooms = bed;
  }
  if (!result.bathrooms) {
    const bath = extractMicrodataNumber(html, "numberOfBathroomsTotal")
      ?? extractMicrodataNumber(html, "numberOfBathrooms");
    if (bath !== null) result.bathrooms = bath;
  }

  // Open Graph fallback for address — many listing pages set og:title to
  // "<address> | <agency>" or similar. Only use when we have nothing.
  if (!result.address) {
    const ogTitle = extractMetaContent(html, "og:title");
    if (ogTitle && ogTitle.length > 5 && ogTitle.length < 200) {
      // Strip agency suffix patterns commonly seen: "| <Agency>", "- <Agency>".
      const stripped = ogTitle
        .replace(/\s*\|\s*[A-Z][A-Za-z& ]+$/, "")
        .replace(/\s*-\s*[A-Z][A-Za-z& ]+$/, "")
        .trim();
      if (/\d/.test(stripped)) {
        // Only set if there's a number — looks address-ish.
        result.address = stripped;
      }
    }
  }

  return result;
}
